// Multi-account workspace store (#1649). Holds a list of signed-in accounts and
// which one is active. The ACTIVE account's tokens are mirrored into the legacy
// localStorage "jwt"/"jwt_refresh" keys so every existing API module
// (client.ts, fleetApi.ts, userApi.ts, platformApi.ts, …) that reads
// localStorage.getItem("jwt") keeps working unchanged — switching accounts just
// swaps which account's tokens are mirrored.

export interface Account {
  sub:         string;
  email:       string;
  label:       string;
  jwt:         string;
  jwt_refresh: string;
}

const ACCOUNTS_KEY = "accounts";
const ACTIVE_KEY    = "activeSub";
const LEGACY_JWT    = "jwt";
const LEGACY_REFRESH = "jwt_refresh";

function parseClaims(jwt: string): Record<string, unknown> {
  try {
    return JSON.parse(atob(jwt.split(".")[1]));
  } catch {
    return {};
  }
}

// #2804 — true when the JWT's `exp` claim is in the past. An expired token in
// localStorage is functionally equivalent to "signed out": the AuthProvider
// must NOT try to fetch /admin/user/me with it, because the backend returns
// 401 and the browser auto-logs "Failed to load resource: 401" to the console
// (browsers log that for every 401 fetch — JS cannot suppress it). Filtering
// expired accounts at read time keeps the SPA rendering the anon UI without
// a noisy bootstrap.
//
// 30s skew tolerance covers clock drift between client + Cognito. Tokens
// without an `exp` claim are passed through (server stays authoritative).
export function isJwtExpired(jwt: string): boolean {
  const claims = parseClaims(jwt);
  const exp = typeof claims.exp === "number" ? claims.exp * 1000 : null;
  if (!exp) return false;
  return exp <= Date.now() - 30_000;
}

// "info@2hives.ai" -> "info's workspace"; falls back to the full email.
export function workspaceLabel(email: string): string {
  const local = email.includes("@") ? email.split("@")[0] : email;
  return local ? `${local}'s workspace` : email;
}

function accountFromTokens(jwt: string, jwt_refresh: string): Account | null {
  const claims = parseClaims(jwt);
  const sub = typeof claims.sub === "string" ? claims.sub : "";
  if (!sub) return null;
  const email =
    (typeof claims.email === "string" && claims.email) ||
    (typeof claims["cognito:username"] === "string" && (claims["cognito:username"] as string)) ||
    sub;
  return { sub, email, label: workspaceLabel(email), jwt, jwt_refresh };
}

function readAccounts(): Account[] {
  try {
    const raw = localStorage.getItem(ACCOUNTS_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    // #2804 — drop accounts whose JWT has already expired so the AuthProvider
    // initialises with token=null and never fires a 401-producing
    // /admin/user/me at bootstrap.
    return Array.isArray(arr)
      ? arr.filter((a) => a && a.sub && a.jwt && !isJwtExpired(a.jwt))
      : [];
  } catch {
    return [];
  }
}

function writeAccounts(accounts: Account[]): void {
  localStorage.setItem(ACCOUNTS_KEY, JSON.stringify(accounts));
}

// Mirror the active account's tokens into the legacy keys (or clear them).
function mirrorActive(account: Account | null): void {
  if (account) {
    localStorage.setItem(LEGACY_JWT, account.jwt);
    localStorage.setItem(LEGACY_REFRESH, account.jwt_refresh);
  } else {
    localStorage.removeItem(LEGACY_JWT);
    localStorage.removeItem(LEGACY_REFRESH);
  }
}

// One-time migration: an existing single-jwt session (pre-#1649) becomes the
// first account in the array. Runs on first load when there's no accounts array.
function migrateLegacy(): Account[] {
  const jwt = localStorage.getItem(LEGACY_JWT);
  const refresh = localStorage.getItem(LEGACY_REFRESH) ?? "";
  if (!jwt) return [];
  // #2804 — never migrate an already-expired legacy JWT; it would just trip
  // a console 401 on the next render. Clear the stale mirror too so the next
  // page load is clean.
  if (isJwtExpired(jwt)) {
    mirrorActive(null);
    return [];
  }
  const acct = accountFromTokens(jwt, refresh);
  if (!acct) return [];
  writeAccounts([acct]);
  localStorage.setItem(ACTIVE_KEY, acct.sub);
  return [acct];
}

// Public read API ────────────────────────────────────────────────────────────

export function listAccounts(): Account[] {
  const accounts = readAccounts();
  if (accounts.length === 0) return migrateLegacy();
  // Keep the active account's mirrored jwt fresh in the array (client.ts refresh
  // writes new tokens into the legacy keys; fold that back into the array).
  return syncActiveFromMirror(accounts);
}

export function getActiveSub(): string | null {
  return localStorage.getItem(ACTIVE_KEY);
}

export function getActiveAccount(): Account | null {
  const accounts = listAccounts();
  if (accounts.length === 0) {
    // #2804 — all accounts were filtered out (expired). Clear the legacy
    // mirror so api/* modules don't keep reading the stale JWT.
    if (localStorage.getItem(LEGACY_JWT)) mirrorActive(null);
    return null;
  }
  const sub = getActiveSub();
  return accounts.find((a) => a.sub === sub) ?? accounts[0];
}

// If client.ts refreshed the legacy "jwt" out from under us, fold the new token
// back into the active account record so it survives a switch.
function syncActiveFromMirror(accounts: Account[]): Account[] {
  const sub = localStorage.getItem(ACTIVE_KEY);
  const mirrorJwt = localStorage.getItem(LEGACY_JWT);
  const mirrorRefresh = localStorage.getItem(LEGACY_REFRESH) ?? "";
  if (!sub || !mirrorJwt) return accounts;
  let changed = false;
  const next = accounts.map((a) => {
    if (a.sub === sub && (a.jwt !== mirrorJwt || a.jwt_refresh !== mirrorRefresh)) {
      changed = true;
      return { ...a, jwt: mirrorJwt, jwt_refresh: mirrorRefresh };
    }
    return a;
  });
  if (changed) writeAccounts(next);
  return next;
}

// Public mutate API ────────────────────────────────────────────────────────────

// Add (or update if same sub) an account from a fresh login and make it active.
// Returns the active account.
export function addAccount(jwt: string, jwt_refresh: string): Account | null {
  const acct = accountFromTokens(jwt, jwt_refresh);
  if (!acct) return null;
  const accounts = listAccounts();
  const existing = accounts.findIndex((a) => a.sub === acct.sub);
  const next = existing >= 0 ? accounts.map((a, i) => (i === existing ? acct : a)) : [...accounts, acct];
  writeAccounts(next);
  localStorage.setItem(ACTIVE_KEY, acct.sub);
  mirrorActive(acct);
  return acct;
}

// Switch the active account. Returns the newly-active account (or null).
export function switchAccount(sub: string): Account | null {
  const accounts = listAccounts();
  const acct = accounts.find((a) => a.sub === sub);
  if (!acct) return null;
  localStorage.setItem(ACTIVE_KEY, acct.sub);
  mirrorActive(acct);
  return acct;
}

// Remove one account. If it was active, fall back to another (made active) or
// null (no accounts left). Returns the new active account (or null).
export function removeAccount(sub: string): Account | null {
  const accounts = listAccounts().filter((a) => a.sub !== sub);
  writeAccounts(accounts);
  const wasActive = getActiveSub() === sub;
  if (!wasActive) return getActiveAccount();
  const fallback = accounts[0] ?? null;
  if (fallback) {
    localStorage.setItem(ACTIVE_KEY, fallback.sub);
    mirrorActive(fallback);
  } else {
    localStorage.removeItem(ACTIVE_KEY);
    mirrorActive(null);
  }
  return fallback;
}

// Clear everything (used on a hard "sign out of all" / unrecoverable expiry).
export function clearAllAccounts(): void {
  localStorage.removeItem(ACCOUNTS_KEY);
  localStorage.removeItem(ACTIVE_KEY);
  mirrorActive(null);
}
